import { useEffect, useState } from 'react';
import {
  Segmented,
  Form,
  Input,
  Button,
  Alert,
  Table,
  Tag,
  Spin
} from 'antd';
import {
  GlobalOutlined,
  PlusOutlined,
  DeleteOutlined,
  SafetyOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  AimOutlined
} from '@ant-design/icons';
import endpoints from '../../api/endpoints';
import { useAppNotifications } from '../../utils/notify';
import { sanitizeText } from '../../utils/sanitize';
import { ipAddressInput } from '../../utils/validators';

// Stage 5L: "Login IP Access" section rendered inside the user details
// panel (UserDetailsPanel) — Access & Security tab.
//
// Inline CRUD model:
//   - Switching mode persists immediately.
//   - Adding an IP persists immediately.
//   - Removing an IP persists immediately.
//   No Save button. The backend PUT /api/users/:id/ip-access replaces
//   the entire whitelist atomically, so each action sends the full
//   desired state. On error the local state is rolled back from the
//   previous snapshot so the UI never desyncs from the server.
//
// Master self-lockout, IP_WHITELIST_EMPTY, INVALID_IP_ADDRESS, and
// IP_LIMIT_EXCEEDED are all enforced server-side and surfaced via the
// existing notify CODE_MAP popups.

// Lightweight IPv4 / IPv6 / CIDR client check for snappy UX. Backend
// (Joi.ip with cidr:'optional') remains the source of truth.
function looksLikeIp(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  const slash = raw.indexOf('/');
  const host = slash >= 0 ? raw.slice(0, slash) : raw;
  const mask = slash >= 0 ? raw.slice(slash + 1) : null;
  if (!host) return false;
  // IPv4 host
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)) {
    const ok = host.split('.').every((p) => Number(p) >= 0 && Number(p) <= 255);
    if (!ok) return false;
    if (mask === null) return true;
    return /^\d+$/.test(mask) && Number(mask) >= 0 && Number(mask) <= 32;
  }
  // IPv6 host (loose — backend Joi.ip() is the strict gate)
  if (host.includes(':') && /^[0-9a-fA-F:.]+$/.test(host) && host.length <= 45) {
    if (mask === null) return true;
    return /^\d+$/.test(mask) && Number(mask) >= 0 && Number(mask) <= 128;
  }
  return false;
}

// Strip a host-mask suffix so '203.0.113.4/32' compares equal to
// '203.0.113.4'. Used for the "current IP is in the list" badge and
// the duplicate-detection check; the backend ipMatchesAnyEntry is the
// authority for the actual mode-switch guard.
function stripHostMask(value) {
  const s = String(value || '').trim();
  const slash = s.indexOf('/');
  if (slash < 0) return s.toLowerCase();
  const host = s.slice(0, slash);
  const mask = s.slice(slash + 1);
  if (mask === '32' && /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host)) return host.toLowerCase();
  if (mask === '128' && host.includes(':')) return host.toLowerCase();
  return s.toLowerCase();
}

export default function UserIpAccessSection({ user, onChanged }) {
  const { notifyError, notifySuccess } = useAppNotifications();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState('any');
  const [entries, setEntries] = useState([]);
  // Server-reported request IP (from getIpAccess), used to render the
  // "Your current IP" pill and the "Use my current IP" quick-add. Never
  // populated from a client claim — always sourced from req.ip on the
  // backend.
  const [currentIp, setCurrentIp] = useState(null);
  const [currentIpMatches, setCurrentIpMatches] = useState(false);
  // Local form state for the "add IP" row.
  const [newIp, setNewIp] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newIpError, setNewIpError] = useState('');

  // Fetch on first render and whenever the target user changes.
  useEffect(() => {
    let cancelled = false;
    if (!user || !user.id) return undefined;
    setLoading(true);
    endpoints.users
      .getIpAccess(user.id)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data && data.whitelist)
          ? data.whitelist
          : [];
        const m = data && data.ipAccessMode === 'whitelist' ? 'whitelist' : 'any';
        setMode(m);
        setEntries(list);
        setCurrentIp(data && data.currentIp ? data.currentIp : null);
        setCurrentIpMatches(Boolean(data && data.currentIpMatchesWhitelist));
        setNewIp('');
        setNewLabel('');
        setNewIpError('');
      })
      .catch((err) => notifyError(err, { title: 'Could not load IP access' }))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user && user.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Single inline-CRUD persist path. Sends the desired full state and
  // updates local from the response. On error, restores the previous
  // snapshot so the UI never drifts from the server.
  async function applyChange({ nextMode, nextEntries, successMessage }) {
    const prevMode = mode;
    const prevEntries = entries;

    // Optimistic local update so the table feels instant.
    setMode(nextMode);
    setEntries(nextEntries);
    setBusy(true);

    try {
      const data = await endpoints.users.updateIpAccess(user.id, {
        ipAccessMode: nextMode,
        whitelist: nextEntries
          .filter((e) => e.isActive !== false)
          .map((e) => ({
            ipAddress: e.ipAddress,
            label: e.label || ''
          }))
      });
      const list = Array.isArray(data && data.whitelist) ? data.whitelist : [];
      const m = data && data.ipAccessMode === 'whitelist' ? 'whitelist' : 'any';
      setMode(m);
      setEntries(list);
      if (data && data.currentIp !== undefined) setCurrentIp(data.currentIp);
      if (data && data.currentIpMatchesWhitelist !== undefined) {
        setCurrentIpMatches(Boolean(data.currentIpMatchesWhitelist));
      }
      if (successMessage) {
        notifySuccess('IP access updated', successMessage);
      }
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      // Rollback so the UI matches the server's reality.
      setMode(prevMode);
      setEntries(prevEntries);
      notifyError(err, { title: 'Could not update IP access' });
    } finally {
      setBusy(false);
    }
  }

  function onModeChange(nextMode) {
    if (nextMode === mode) return;
    if (nextMode === 'whitelist' && entries.filter((e) => e.isActive !== false).length === 0) {
      // Friendly client-side guard so the master gets a clean popup
      // before the backend round-trip. Server enforces this too.
      notifyError(
        { code: 'IP_WHITELIST_EMPTY' },
        { title: 'Whitelist is empty' }
      );
      return;
    }
    // Pre-flight master-self-lockout check — uses the server-computed
    // currentIpMatchesWhitelist flag so we honour CIDR containment
    // correctly. Saves a 400 round-trip and gives the operator a
    // single-click recovery via "Use my current IP" below.
    if (
      nextMode === 'whitelist' &&
      user &&
      user.isMaster &&
      currentIp &&
      !currentIpMatches
    ) {
      notifyError(
        { code: 'CURRENT_IP_NOT_WHITELISTED' },
        {
          title: 'Current IP not added',
          description:
            `Your current IP ${currentIp} is not in the whitelist. ` +
            'Click "Use my current IP" below to add it, then try again.'
        }
      );
      return;
    }
    applyChange({
      nextMode,
      nextEntries: entries,
      successMessage:
        nextMode === 'whitelist'
          ? `${user.displayName || user.email} can now sign in only from listed IPs.`
          : `${user.displayName || user.email} can sign in from any IP.`
    });
  }

  function tryAddEntry() {
    const ip = String(newIp || '').trim();
    const label = sanitizeText(newLabel, {
      collapseWhitespace: true,
      maxLength: 100
    });
    const active = entries.filter((e) => e.isActive !== false);

    if (!ip) {
      setNewIpError('Enter an IP address');
      return;
    }
    if (!looksLikeIp(ip)) {
      setNewIpError('Enter a valid IPv4 or IPv6 address');
      return;
    }
    // Case-fold AND strip host-mask suffix when checking duplicates so
    // '203.0.113.4' and '203.0.113.4/32' are treated as the same row.
    const newKey = stripHostMask(ip);
    if (active.some((e) => stripHostMask(e.ipAddress) === newKey)) {
      setNewIpError('That IP is already in the list');
      return;
    }
    if (active.length >= 20) {
      setNewIpError('Maximum 20 IPs per user');
      return;
    }

    const nextEntries = [
      ...entries,
      { ipAddress: ip, label, isActive: true }
    ];
    setNewIpError('');
    applyChange({
      nextMode: mode,
      nextEntries,
      successMessage: `Added ${ip}.`
    }).then(() => {
      // Clear form on persisted success — applyChange resolves both
      // success and rollback paths, so detect via state. We just
      // optimistically clear; if the server rejected, the rollback
      // already restored entries, but the form being empty is fine
      // since the user can retype.
      setNewIp('');
      setNewLabel('');
    });
  }

  function removeEntry(entry) {
    if (mode === 'whitelist') {
      const remaining = entries.filter(
        (e) => !(e.ipAddress === entry.ipAddress && e.label === entry.label)
      );
      const remainingActive = remaining.filter((e) => e.isActive !== false);
      if (remainingActive.length === 0) {
        // Removing the last IP while in whitelist mode would lock the
        // user out. Backend rejects with IP_WHITELIST_EMPTY but show
        // the friendlier popup before the round-trip.
        notifyError(
          { code: 'IP_WHITELIST_EMPTY' },
          {
            title: 'Cannot remove last IP',
            description:
              'Removing the last whitelisted IP while in whitelist-only mode would lock this user out. Switch to "Any IP" first or add another IP before removing this one.'
          }
        );
        return;
      }
    }
    const nextEntries = entries.filter(
      (e) => !(e.ipAddress === entry.ipAddress && e.label === entry.label)
    );
    applyChange({
      nextMode: mode,
      nextEntries,
      successMessage: `Removed ${entry.ipAddress}.`
    });
  }

  const submittedActive = entries.filter((e) => e.isActive !== false);

  if (loading) {
    return (
      <div className="rounded-xl border border-surface-200 p-4 mt-4">
        <div className="flex items-center gap-2 text-sm text-ink-500">
          <Spin size="small" /> Loading IP access…
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-xl border border-surface-200 bg-white p-3.5">
      <div className="flex items-start gap-2 mb-3">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-brand-50 text-brand-600 shrink-0">
          <SafetyOutlined />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-ink-900 m-0 leading-tight">
              Login IP access
            </h3>
            <span className="inline-flex items-center gap-1 text-[10.5px] uppercase tracking-[0.12em] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-1.5 py-0.5 font-semibold">
              Auto-saves
            </span>
          </div>
          <p className="text-xs text-ink-500 m-0 mt-0.5">
            Choose which networks this user can sign in from. Existing
            sessions are not affected.
          </p>
        </div>
      </div>

      {/* Current-IP pill — server-sourced from req.ip so the operator
          can see what address the request is actually coming from
          without leaving the app. Also acts as the recovery target for
          the "Use my current IP" quick-add below. */}
      {currentIp ? (
        <div
          className={[
            'flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5 mb-3 text-xs',
            currentIpMatches
              ? 'border-emerald-200 bg-emerald-50/60'
              : 'border-amber-200 bg-amber-50/70'
          ].join(' ')}
          role="status"
          aria-live="polite"
        >
          <div className="flex items-center gap-1.5 min-w-0">
            {currentIpMatches ? (
              <CheckCircleFilled className="text-emerald-600 shrink-0" />
            ) : (
              <ExclamationCircleFilled className="text-amber-600 shrink-0" />
            )}
            <span className="text-ink-700">Your current IP:</span>
            <code className="font-mono text-ink-900 truncate">{currentIp}</code>
            <span
              className={
                currentIpMatches
                  ? 'inline-flex items-center gap-1 ml-1 rounded-md border border-emerald-200 bg-white px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-emerald-700'
                  : 'inline-flex items-center gap-1 ml-1 rounded-md border border-amber-200 bg-white px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-amber-700'
              }
            >
              {currentIpMatches ? 'In whitelist' : 'Not in whitelist'}
            </span>
          </div>
          {!currentIpMatches ? (
            <Button
              size="small"
              type="link"
              icon={<AimOutlined />}
              onClick={() => {
                setNewIp(currentIp);
                setNewLabel((prev) => (prev && prev.trim()) ? prev : 'My current network');
                setNewIpError('');
              }}
              disabled={busy}
              className="!px-1.5 !text-[12px] !text-blue-600 hover:!text-blue-700"
            >
              Use my current IP
            </Button>
          ) : null}
        </div>
      ) : null}

      <Form layout="vertical" size="small" disabled={busy}>
        <Form.Item
          label={
            <span className="text-[11px] font-semibold text-ink-500 uppercase tracking-[0.14em]">
              Access mode
            </span>
          }
          className="!mb-2"
        >
          <Segmented
            block
            className="glass-ip-access-segmented"
            value={mode}
            onChange={onModeChange}
            disabled={busy}
            options={[
              {
                label: (
                  <span className="flex items-center justify-center gap-1.5 py-0.5">
                    <GlobalOutlined /> Any IP
                  </span>
                ),
                value: 'any'
              },
              {
                label: (
                  <span className="flex items-center justify-center gap-1.5 py-0.5">
                    <SafetyOutlined /> Whitelisted IPs only
                  </span>
                ),
                value: 'whitelist'
              }
            ]}
          />
        </Form.Item>

        {mode === 'any' ? (
          <Alert
            type="info"
            showIcon
            className="!mb-3 !py-1.5 !text-[12px]"
            message={
              submittedActive.length > 0
                ? 'Sign-ins are currently allowed from any IP. The list below is configured but not enforced — switch to "Whitelisted IPs only" to enforce it.'
                : 'Sign-ins are currently allowed from any IP. Add IPs below first if you plan to switch to whitelist-only mode.'
            }
          />
        ) : null}

        {mode === 'whitelist' && submittedActive.length === 0 ? (
          <Alert
            type="warning"
            showIcon
            className="!mb-3 !py-1.5 !text-[12px]"
            message="No IP addresses configured. The user will be blocked at login until at least one IP is added."
          />
        ) : null}

        {/* Whitelist table — visible in BOTH modes so a master can
            pre-configure IPs while still in "Any IP" mode and then
            switch to whitelist enforcement once the list is ready.
            Mode only controls enforcement. */}
        {(mode === 'whitelist' || submittedActive.length > 0) ? (
          <div className="rounded-lg border border-surface-200 overflow-hidden mb-3">
            <Table
              size="small"
              rowKey={(record) => record.id || record.ipAddress}
              dataSource={submittedActive}
              pagination={false}
              tableLayout="auto"
              loading={busy}
              locale={{
                emptyText: (
                  <div className="text-xs text-ink-500 py-2">
                    No whitelisted IPs added yet.
                  </div>
                )
              }}
              columns={[
                {
                  title: 'IP Address',
                  dataIndex: 'ipAddress',
                  render: (v) => (
                    <code className="text-xs font-mono text-ink-900">{v}</code>
                  )
                },
                {
                  title: 'Label',
                  dataIndex: 'label',
                  render: (v) =>
                    v ? (
                      <span className="text-xs text-ink-700 truncate">{v}</span>
                    ) : (
                      <span className="text-xs text-ink-400">—</span>
                    )
                },
                {
                  title: 'Status',
                  key: 'status',
                  width: 96,
                  render: () => (
                    <Tag
                      color="success"
                      className="!m-0 !leading-5 !text-[11px]"
                    >
                      Active
                    </Tag>
                  )
                },
                {
                  title: 'Action',
                  key: 'action',
                  width: 96,
                  align: 'right',
                  render: (_v, entry) => (
                    <Button
                      size="small"
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => removeEntry(entry)}
                      disabled={busy}
                      aria-label={`Remove ${entry.ipAddress}`}
                    >
                      Remove
                    </Button>
                  )
                }
              ]}
            />
          </div>
        ) : null}

        {/* Add IP row — visible in BOTH modes so the master can
            populate the list before flipping to whitelist enforcement.
            Solid-bordered card matches the rest of the app's section
            cards (no more "draft" dashed border). */}
        <div className="rounded-lg border border-surface-200 bg-surface-50/40 p-3">
          <h4 className="text-[11px] font-semibold text-ink-500 uppercase tracking-[0.14em] m-0 mb-2">
            Add a whitelisted IP
          </h4>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              size="middle"
              value={newIp}
              onChange={(e) => {
                // IP mask: strip anything that isn't a digit, hex
                // letter (a-f/A-F), dot, or colon — and cap at 45.
                // Stops typos like letters g-z from ever appearing
                // in the field. Hex letters ARE allowed because
                // they're valid IPv6 characters; the live-validate
                // check below catches strings that pass the char
                // filter but aren't a real IP shape (e.g. "adadad").
                const next = ipAddressInput(e);
                setNewIp(next);
                if (!next) {
                  setNewIpError('');
                } else if (!looksLikeIp(next)) {
                  setNewIpError('Enter a valid IPv4 or IPv6 address');
                } else {
                  setNewIpError('');
                }
              }}
              onPressEnter={(e) => {
                e.preventDefault();
                tryAddEntry();
              }}
              placeholder="e.g. 103.25.12.10 or 203.0.113.0/24"
              className="sm:!flex-1"
              status={newIpError ? 'error' : undefined}
              disabled={busy}
              maxLength={45}
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              prefix={<GlobalOutlined className="text-ink-400" />}
            />
            <Input
              size="middle"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onPressEnter={(e) => {
                e.preventDefault();
                tryAddEntry();
              }}
              placeholder="Office, Factory, Home…"
              maxLength={100}
              className="sm:!flex-1"
              disabled={busy}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={tryAddEntry}
              disabled={busy || !newIp || !looksLikeIp(newIp)}
              loading={busy}
              className="!h-9 !rounded-lg !font-medium !bg-[#1877F2] hover:!bg-blue-700 !border-0 !shadow-sm"
            >
              Add
            </Button>
          </div>
          {newIpError ? (
            <p className="text-xs text-rose-600 m-0 mt-1.5">{newIpError}</p>
          ) : (
            <p className="text-xs text-ink-500 m-0 mt-1.5">
              Public static IP, or a CIDR subnet (e.g. <code className="font-mono">203.0.113.0/24</code>) for an office network. Up to 20 entries.
            </p>
          )}
        </div>
      </Form>
    </div>
  );
}
